import { maxWithReducer } from 'shared/src/utils/maxByReducer';
import { meanReducerFactory } from 'shared/src/utils/meanReducer';
import { linearRegression } from './linearRegression';
import Note from './note';

const debug = false;
function log3DArray(name: string, arr: number[][]) {
  if (!debug) return;
  let str = name;
  str += ':\n[\n';
  str += arr
    .map((subArr) => `  [ ${subArr.map((n) => Math.round(n * 10) / 10).join(', ')} ],`)
    .join('\n');
  str += '\n]';
  console.log(str);
}

const lowestMidi = 36;
// const highestMidi = 86;

interface StableMeterOptions {
  /** In seconds. Over what period of time should we average the input? */
  timeWindowSeconds: number;
  /** Used to calculate the time window in which to average. Default: 48khz */
  sampleRate: number;
  /** Used to calculate the time window in which to average. Default: 1024 */
  bufferSize: number;
  /** Highest acceptable note */
  highestMidi: number;
  /** Lowest acceptable note */
  lowestMidi: number;
}

export default class PitchDetectionAverager {
  /** -1 if no valid pitch */
  private meanMidi: number = -1;

  /** Excluding those outside of n SD */
  private meanMidiExcludingOutliers: number = -1;

  /**
   * Maybe we have several notes, perhaps by misshearing an overtone or
   * because the note changed during the averaging window.
   */
  private meanFromClusters: number = -1;

  /**
   * Number of pitches in each StableMeter frame.
   * Calculated as (sampleRate / bufferSize) * timeWindow
   */
  private frameCountToAverage: number;

  /** Midi numbers (non-integer!) converted from freq input to de-logrhythmicize pitch */
  private midiFrames: number[] = [];

  private options: StableMeterOptions;

  constructor(options?: Partial<StableMeterOptions>) {
    this.options = {
      timeWindowSeconds: 0.2,
      sampleRate: 48000,
      bufferSize: 1024,
      highestMidi: new Note('C6').getMidiNumber(),
      lowestMidi: new Note('C2').getMidiNumber(),
      ...options,
    };

    // console.log(this.options);

    this.frameCountToAverage = Math.round(
      (this.options.timeWindowSeconds * this.options.sampleRate!) / this.options.bufferSize!
    );

    if (debug) console.log({ frameCountToAverage: this.frameCountToAverage });
  }

  get size() {
    return this.midiFrames.length;
  }

  addMidi(midiNumber: number): {
    medianClusterAverage: number;
    frequencyAverage: number;
    midiAverage: number;
    midiAverageExcludingOutliers: number;
    midiAverageFromClusters: number;
    regressionMidiAverage: number;
    regressionFrequencyAverage: number;
    gradient: number;
    intercept: number;
    r2: number;
    frames: number[];
  } {
    // console.log(
    //   `Midi: ${Math.round(midiNumber)}, Max: ${this.options.highestMidi}, Min: ${
    //     this.options.lowestMidi
    //   }`
    // );
    // If the note is out of range, but we have an octave cluster above or below,
    // then let's assume it's that octave.
    if (midiNumber !== -1 && midiNumber < this.options.lowestMidi) {
      const halfStepClusters = getClustersWithinNHalfSteps(this.midiFrames);
      // console.log(`midi out of bounds ${Math.round(midiNumber)}, ${Object.keys(halfStepClusters)}`);

      if (
        halfStepClusters[Math.round(midiNumber) + 12] ||
        halfStepClusters[Math.round(midiNumber) + 11] ||
        halfStepClusters[Math.round(midiNumber) + 13]
      ) {
        // console.log('adjusting octave for out-of-range');
        midiNumber += 12;
      } else {
        // console.log('but no matching cluster found.');
        midiNumber = -1;
      }
    }
    if (midiNumber !== -1 && midiNumber > this.options.highestMidi) {
      const halfStepClusters = getClustersWithinNHalfSteps(this.midiFrames);
      // console.log(`midi out of bounds ${Math.round(midiNumber)}, ${Object.keys(halfStepClusters)}`);
      if (
        halfStepClusters[Math.round(midiNumber) - 12] ||
        halfStepClusters[Math.round(midiNumber) - 11] ||
        halfStepClusters[Math.round(midiNumber) - 13]
      ) {
        // console.log('adjusting octave for out-of-range');
        midiNumber -= 12;
      } else {
        // console.log('but no matching cluster found.');
        midiNumber = -1;
      }
    }

    // If we note this midi is an octave above the previous midi, then it's EXTREMELY
    // likely an overtone was recorded. Changing notes would have resulted in some other
    // noise somewhere in the process for singing. So... we drop it an octave.
    const lastMidi = this.midiFrames[this.midiFrames.length - 1];
    const bothArePitched = lastMidi && lastMidi !== -1 && midiNumber !== -1;
    if (bothArePitched && isOvertone(midiNumber, lastMidi)) {
      midiNumber /= 2;
    }

    if (bothArePitched && midiNumber < lowestMidi && isOvertone(lastMidi, midiNumber)) {
      midiNumber *= 2;
    }

    this.midiFrames.push(midiNumber);
    if (this.midiFrames.length > this.frameCountToAverage) this.midiFrames.shift();
    this.updateMidiMeans();
    const { gradient, intercept, r2, predict } = this.calculateStability();
    const regressionMidiAverage = predict(this.midiFrames.length / 2)[1];

    return {
      medianClusterAverage: getAverageFromClusterAroundMedian(this.midiFrames),
      midiAverage: this.meanMidi,
      midiAverageExcludingOutliers: this.meanMidiExcludingOutliers,
      midiAverageFromClusters: this.meanFromClusters,
      frequencyAverage: Note.midiToFrequency(this.meanMidi),
      regressionMidiAverage,
      regressionFrequencyAverage: Note.midiToFrequency(regressionMidiAverage),
      gradient,
      intercept,
      r2,
      frames: this.midiFrames,
    };
  }

  /**
   * @return [gradient, Pearson correlation]. [Infinity, 0] if not enough data has been collected to determine
   * if the pitch stream is stable
   */
  addFrequency(freq: number) {
    if (freq === -1) {
      return this.addMidi(-1);
    }
    const newMidiNo = Note.frequencyToMidi(freq);
    return this.addMidi(newMidiNo);
  }

  /** Find the gradient and pearson coefficient (r^2) from the current midi means */
  private calculateStability() {
    const pairs: [number, number][] = this.midiFrames.map((y, x) => [x, y]);
    return linearRegression(pairs);
  }

  /**
   * Calculate the average midi. -1 if no pitch recognized.
   */
  private updateMidiMeans(): void {
    if (!this.midiFrames.length) return;

    const groups = getClusteredMidiGroups(this.midiFrames);
    const mean = predictMeanFromClusters(groups);
    // console.log('midiFrames', this.midiFrames);
    // console.log('groups', groups);
    // console.log('mean', mean);
    this.meanFromClusters = mean;

    // If half of our frames are not pitched, the whole thing is not pitched:
    const noPitchCount = this.midiFrames.filter((n) => n === -1).length;
    if (noPitchCount > this.midiFrames.length / 2) {
      this.meanMidi = -1;
      this.meanMidiExcludingOutliers = -1;

      return;
    }

    const pitchMidis = this.midiFrames.filter((n) => n !== -1);
    this.meanMidi = pitchMidis.reduce((a, b) => a + b) / pitchMidis.length;
    this.meanMidiExcludingOutliers = getAverageWithoutOutliers(pitchMidis, 1);
  }

  static getMidiFromFrequency(freq: number) {
    return 69 + 12 * Math.log2(freq / 440);
  }
}

/**
 * Returns midi frames grouped in windows within n * windowRadiusHalfSteps of recorded midi.
 * @param midiFrames
 * @param windowRadiusHalfSteps
 * @returns
 */
function getClustersWithinNHalfSteps(
  midiFrames: number[],
  windowRadiusHalfSteps = 2
): {
  [centerMidiNumber: number]: number[];
} {
  return midiFrames.reduce((windowCounts: { [centerMidiNumber: number]: number[] }, midiNumber) => {
    const roundedMidi = Math.round(midiNumber);
    for (let radius = -windowRadiusHalfSteps; radius <= windowRadiusHalfSteps; radius++) {
      windowCounts[roundedMidi + radius] = [
        ...(windowCounts[roundedMidi + radius] || []),
        midiNumber,
      ];
    }
    return windowCounts;
  }, {});
}

/**
 * Groups midi frames into windows of windowRadiusHalfSteps * 2 width,
 * then find the group with the most frames and return its average.
 * @param midiFrames
 * @param windowRadiusHalfSteps
 * @returns
 */
function getAverageFromClusterAroundMedian(midiFrames: number[], windowRadiusHalfSteps = 2) {
  if (windowRadiusHalfSteps < 0 || Math.round(windowRadiusHalfSteps) !== windowRadiusHalfSteps) {
    throw Error(`Cluster window radius must be a positive integer, not ${windowRadiusHalfSteps}`);
  }

  const midiWindowClusterDictionary = getClustersWithinNHalfSteps(
    midiFrames,
    windowRadiusHalfSteps
  );

  const maxMidiCluster = Object.entries(midiWindowClusterDictionary).reduce(
    maxWithReducer(([, count]) => count.length),
    ['-1', [0]]
  );

  return maxMidiCluster[1].reduce(meanReducerFactory(), 0);
}

/**
 * Return the average based on all data that falls within n SD of mean
 */
function getAverageWithoutOutliers(midiFrames: number[], nStandardDeviations = 3) {
  const length = midiFrames.length;
  if (length === 0) return -1;
  const { sum, sumSquares } = midiFrames.reduce(
    (accum, midiNumber) => {
      accum.sum += midiNumber;
      accum.sumSquares += midiNumber ** 2;
      return accum;
    },
    { sum: 0, sumSquares: 0 }
  );
  const mean = sum / length;
  const varience = sumSquares / length - mean ** 2;
  const standardDev = Math.sqrt(varience);
  const validMidiNumbers = midiFrames.filter(
    (midiNumber) =>
      midiNumber >= mean - nStandardDeviations * standardDev &&
      midiNumber <= mean + nStandardDeviations * standardDev
  );
  return validMidiNumbers.reduce((a, b) => a + b, 0) / validMidiNumbers.length;
}

function getClusteredMidiGroups(midiFrames: number[]) {
  // First sort them:
  midiFrames = midiFrames.slice(0).sort();
  const minMidi = midiFrames.find((midi) => midi > 0) || -1;
  const maxMidi = midiFrames[midiFrames.length - 1] ?? -1;
  const range = maxMidi - minMidi;

  // If the range is zero, save time:
  if (range === 0) return [[minMidi]];

  // See https://stackoverflow.com/a/17480086/1209850 for inspiration
  // Calculate adjacent differences as percentage of prior number in array; initialize diffs[0] to midiFrames[0]
  const clusters = midiFrames
    .map((b, i) => {
      // The first number will always begin a group:
      if (i === 0) return 1;
      const a = midiFrames[i - 1]!;
      // Calculate the difference of the two numbers as a percentage of the range of all numbers:
      return (b - a) / range;
    })
    // Find the breakpoints & group midiFrames accordingly:
    .reduce((groups: number[][], diff, i): number[][] => {
      const midiFrame = midiFrames[i]!;
      // Start a new group if needed:
      if (diff > 0.4) {
        groups.push([midiFrame]);
      } else {
        groups[groups.length - 1]!.push(midiFrame);
      }
      return groups;
    }, []);

  if (clusters.length > 1) {
    log3DArray('Clusters', clusters);
  }

  return clusters;
}

function predictMeanFromClusters(midiFrameGroups: number[][]) {
  // Get the average for each cluster:
  const means = midiFrameGroups.map((group) => getAverageWithoutOutliers(group, 2));

  if (midiFrameGroups.length > 1 && debug) {
    console.log('means', means);
  }
  // Find overtone-grouped clusters; the groups are already sorted low to high.
  // We'll map to [fundamentalIndex, ...overtoneIndexes][]
  /**
   * Indexes from the sorted midiFrameGroups grouped into overtones as:
   * [
   *    [midiFrameGroupsIndex1, Index2],
   *    [midiFrameGroupsIndex3]
   * ]
   */
  const overtoneGroups = means.reduceRight((groups: number[][], overtone, meansI): number[][] => {
    // If the overtone's already been included, don't check:
    if (groups.find((group) => group.find((i) => i === meansI))) {
      // console.log(
      //   `means group index ${meansI}, overtone ${overtone}, already added to ${groups.join('\n')}`
      // );
      return groups;
    }

    // Let's find all its undertones!
    const group = means
      .map((fundamental, i) => (isOvertone(overtone, fundamental) ? i : null))
      .filter((i): i is number => i !== null);
    groups.push(group);
    return groups;
  }, []);

  if (midiFrameGroups.length > 1) {
    log3DArray('overtoneGroups', overtoneGroups);
  }

  // Convert them into groups of midi frames, transposing as needed!
  /**
   * Now becomes groups of midi numbers, transposing overtones:
   * ```typescript
   * [
   *    [56, 56, 55.8],
   *    [61],
   * ]
   * ```
   */
  const overtoneMidiGroups = overtoneGroups.map((overtoneGroup) =>
    overtoneGroup
      .map((meansI, i) => {
        const frames = midiFrameGroups[meansI]!;
        // If this is the fundamental, no transposition is needed:
        if (i === 0) return frames;
        // Otherwise, transpose. We're working in midiNumbers, so we can just add/subtract:
        const overtone = findOvertone(means[meansI]!, means[overtoneGroup[0]!]!);
        return frames.map((midi) => midi - overtone!);
      })
      // Now flatten the group to be one dimensional:
      .flat()
  );

  if (midiFrameGroups.length > 1) {
    log3DArray('overtoneMidiGroups', overtoneMidiGroups);
  }

  const largestGroup = overtoneMidiGroups.sort((a, b) => b.length - a.length)[0];

  const avgFromClusters = getAverageWithoutOutliers(largestGroup!, 2);
  if (midiFrameGroups.length > 1 && debug) {
    console.log(`avgFromClusters: ${avgFromClusters}`);
  }

  return avgFromClusters;
}

const overtoneIntervalClasses = [12, 19, 24, 28, 31, 36];

function isOvertone(overtone: number, fundamental: number) {
  const overtoneIC = findOvertone(overtone, fundamental);
  return overtoneIC !== undefined;
}

function findOvertone(overtone: number, fundamental: number) {
  const intervalClass = Math.abs(overtone - fundamental);
  return overtoneIntervalClasses.find((ic) => Math.abs(ic - intervalClass) < 1);
}

// console.log(isOvertone);
